<template>
  <lf-modal
    :title="isEditMode ? t('PRESETS.EDIT_PRESET') : t('PRESETS.ADD_PRESET')"
    :close="close"
    large
  >
    <template #content>
      <form @submit="submit" class="p-6">
        <div class="h-full w-full space-y-4">
          <lf-input
            :placeholder="$t('PRESETS.PRESET_NAME')"
            name="name"
            data-cy="user-modal-name"
          />
          <lf-textarea
            name="description"
            class="h-30 w-full resize-none rounded-sm border-gray-200 placeholder-gray-300"
            rows="3"
            noresize
            data-cy="user-modal-description"
            :placeholder="$t('COMMON.DESCRIPTION')"
            :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
          />
          <user-modal-clients-list
            v-if="isLendflowUser"
            v-model="presetModalValues.assigned_client_ids"
            data-cy="user-modal-clients-list"
            :title="$t('COMMON.ASSIGNMENT')"
            has-border
          />
          <error-message
            name="assigned_client_ids"
            class="pl-2 text-xxs text-red-700 sm:text-xs"
          />
          <assign-workflow-templates
            v-model="presetModalValues.assigned_workflow_template_ids"
            :assigned-client-ids="presetModalValues.assigned_client_ids"
            :preset-wfb-templates="initialValues.assigned_workflow_templates"
            data-cy="assign-workflow-template"
          />
          <error-message
            name="assigned_workflow_template_ids"
            class="pl-2 text-xxs text-red-700 sm:text-xs"
          />
        </div>
        <div class="flex justify-end">
          <modal-buttons
            class="bg-white"
            :on-cancel="close"
            :disabled="isSubmitting"
            save-is-submit
          />
        </div>
      </form>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { type PropType, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import { ErrorMessage, useForm } from "vee-validate";

import { TEXT_CHARACTER_LIMIT_DEFAULT } from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import {
  useCreatePermissionPresetMutation,
  usePermissionsPresetQuery,
  useUpdatePermissionPresetMutation,
} from "@/hooks/queries/usePermissionsPresets";

import AssignWorkflowTemplates from "@/components/presets/presetModal/AssignWorkflowTemplates.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import UserModalClientsList from "@/views/profile/modals/UserModalClientsList.vue";

import type { IClient } from "@/models/clients";
import type { PermissionsPreset } from "@/models/permissionsPreset";

const props = defineProps({
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
  presetData: {
    type: Object as PropType<PermissionsPreset>,
    default: () => ({
      name: "",
      description: "",
      assigned_client_ids: [],
      assigned_workflow_template_ids: [],
      assigned_clients: [],
    }),
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { isLendflowUser } = useAuth();

const { data: fetchedPresetData } = usePermissionsPresetQuery(
  props.isEditMode ? props.presetData.id : ""
);

const { mutateAsync: createPreset } = useCreatePermissionPresetMutation();
const { mutateAsync: updatePreset } = useUpdatePermissionPresetMutation();

const initialValues = computed(() => {
  const sourceData = props.isEditMode
    ? fetchedPresetData.value
    : props.presetData;
  if (!sourceData) {
    return {
      name: "",
      description: "",
      assigned_client_ids: [],
      assigned_workflow_template_ids: [],
      assigned_workflow_templates: [],
    };
  }
  const data = cloneDeep({
    ...sourceData,
    assigned_workflow_template_ids: compact(
      sourceData.assigned_workflow_template_ids
    ),
    assigned_client_ids:
      sourceData.assigned_clients?.map((client: IClient) => client.id) || [],
    assigned_workflow_templates: sourceData.assigned_workflow_templates || [],
  });
  return data;
});

const {
  handleSubmit,
  isSubmitting,
  setFieldError,
  values: presetModalValues,
  setFieldValue,
} = useForm({
  initialValues,
});

type FormFields =
  | "name"
  | "description"
  | "assigned_client_ids"
  | "assigned_workflow_template_ids";

const submit = handleSubmit(async (values) => {
  const payload: Partial<PermissionsPreset> = {
    name: values.name,
    description: values.description,
    assigned_workflow_template_ids:
      presetModalValues.assigned_workflow_template_ids,
  };

  if (isLendflowUser) {
    payload.assigned_client_ids = presetModalValues.assigned_client_ids;
  }

  try {
    if (props.isEditMode) {
      payload.id = props.presetData.id;
      await updatePreset(payload);
    } else {
      await createPreset(payload);
    }
    props.close();
  } catch (error) {
    const errors = error as { formattedErrors: Record<string, string> };
    if (errors.formattedErrors) {
      const specialFieldNames: FormFields[] = [
        "assigned_workflow_template_ids",
        "assigned_client_ids",
      ];

      const formFieldNames = Object.keys(initialValues.value) as FormFields[];

      Object.entries(errors.formattedErrors).forEach(([key, message]) => {
        const fieldName =
          specialFieldNames.find((name) => key.includes(name as string)) || key;

        if (formFieldNames.includes(fieldName as FormFields)) {
          setFieldError(fieldName as FormFields, message);
        }
      });
    }
  }
});

watch(
  initialValues,
  (values) => {
    if (values) {
      setFieldValue("name", values.name || "");
      setFieldValue("description", values.description || "");
      setFieldValue("assigned_client_ids", values.assigned_client_ids || []);
      setFieldValue(
        "assigned_workflow_template_ids",
        values.assigned_workflow_template_ids || []
      );
    }
  },
  { immediate: true }
);
</script>
